<template>
  <span class="text-left">
    <ed-button class="btn btn-actionbar"
               @click="showModal(showAddCard)"
               :disabled="cardActionDisabled('addCard')"
               v-tooltip="actionDisabledReason('addCard')">
      Add Card
    </ed-button>
    <form ref="formRef">
      <ed-modal title="Add Card" :show="showAddCard" @close="showModal(showAddCard)" validation @validate='addCard' primary-button="Add" :primary-button-disabled="savingCardInProgress" @confirm="addCard">
        <div slot="body">
          <div class="add-card-overlay">
            <p class="small">Select the card holder below and the amount you'd like to put on the card. You can request more funds to be added on the card later.</p>
            <div class="form-group">
              <label v-if="accountId === ''" for="division">{{divisionsName}}</label>
              <ed-select @input="(o) => {selectedAccountId = o.AccountId; selectedDivisionId = o.Id; selectUser = ''; selectUsers = []}"
                         v-if="accountId === ''"
                         :placeholder="'Select an Eved ' + divisionName.toLowerCase() + '...'"
                         :options="divisions"
                         v-model="selectDivision"
                         label="Name"
                         track-by="Id"
                         @open="fetchUserDivisions"
                         name="supplier_user" />
              <label for="cardHolder">Card Holder</label>
              <div class="form-group">
                <ed-select placeholder="Select an Eved user..."
                           :options="card.users"
                           @open="onOpenCard"
                           @scroll-bottom="() => loadUsers('card', false)"
                           @search="term => { card.searchTerm = term; loadUsers('card', true) }"
                           @input="onSelectUser"
                           v-model="selectUser"
                           label="Name"
                           track-by="Id"
                           :error="holderIncomplete"
                           :disabled="accountId === '' && selectedAccountId === ''"
                           name="supplier_user" />
              </div>
              <div class="form-group">
                <label>Mobile Phone Number - <span class="optional-text">optional</span></label>
                <div slot="additionalText" style="font-size: smaller; font-style: italic;">
                  The card holder can select to receive one-time passwords via text message
                </div>
                <div class="row">
                  <div class="col-md-3">
                    <vue-country-code @onSelect="onCountrySelect"
                                      ref="countryCodeComponent"
                                      :disabledFetchingCountry="true"
                                      :enabledCountryCode="true"
                                      :preferredCountries="['us', 'gb', 'cn']"
                                      :disabled="!selectUser"
                                      class="dropdown" />
                  </div>
                  <div class="col-md-9">
                    <ed-input onlyNumbers
                              placeholder="222 333 1234"
                              v-model="addObjectUser.Holder.Mobile.PhoneNumber"
                              :error="cardHolderPhoneNumberError"
                              :disabled="!selectUser"
                              class="full-width">
                      <div slot="message" v-if="cardHolderPhoneNumberError">
                        {{ errorPhoneMessage }}
                      </div>
                    </ed-input>
                  </div>
                </div>
              </div>
              <div class="form-group">
                <label>Date Of Birth - <span class="optional-text">optional</span></label>
                <div slot="additionalText" style="font-size: smaller; font-style: italic;">
                  The card holder can select to receive one-time passwords via call center
                </div>
                <ed-datepicker placeholder='Select date'
                               v-model='addObjectUser.Holder.DateOfBirth'
                               :disabled="!selectUser"
                               :format="dateFormat"></ed-datepicker>
              </div>
              <div v-if="$root.getUser.$.userPermissions.includes('Administration') || $root.getUser.$.userPermissions.includes('EvedPayCardAdministrator')">
                <ed-button class="btn btn-actionbar"
                           @click="showModalUser(showAddUser)"
                           :disabled="cardActionDisabled('addUser')"
                           v-tooltip="actionDisabledReason('addUser')">
                  Add User
                </ed-button>
                <AddUser :show="showAddUser"
                         @close="closeAddUserModal" />
              </div>
            </div>
            <div class="form-group">
              <label for="cardAdmin">Admin - <span class="optional-text">optional</span></label>
              <ed-select placeholder="Select an Eved user..."
                         :options="admin.users.filter(filterAdminOptions)"
                         :checkboxes="false"
                         @open="onOpenAdmin"
                         @scroll-bottom="() => loadUsers('admin', false)"
                         @search="term => { admin.searchTerm = term; loadUsers('admin', true) }"
                         @input="o => addObjectUser.AdditionalUsers = o"
                         v-model="selectUsers"
                         label="Name"
                         multiple
                         track-by="Id"
                         :disabled="accountId === '' && selectedAccountId === ''"
                         name="supplier_user" />
            </div>

            <div class="form-group">
              <label for="initialLoad">Initial Load</label>
              <ed-input @input="(o) => { addObjectUser.initialLoad = +o; this.initialLoadError = false; }"
                        type="money"
                        amount
                        :error='initialLoadError'
                        v-model='initialLoad'
                        id="initialLoad"
                        helptip-header=''
                        class="full-width">
                <div slot="message" v-if="initialLoadError">
                  {{ errorMessage }}
                  <strong>
                    <v-money>{{account.Available}}</v-money>
                  </strong>
                </div>
              </ed-input>
            </div>

            <div class="form-group">
              <ed-input @input="(o) => RefillSettings.RefillThreshold = +o"
                        type="money"
                        field-label="Refill Threshold"
                        amount
                        v-model="RefillSettings.RefillThreshold"
                        id="refillThreshold"
                        class="full-width" />
            </div>

            <div class="form-group">
              <ed-input @input="(o) => RefillSettings.RefillAmount = +o"
                        type="money"
                        field-label="Refill Amount"
                        amount
                        v-model="RefillSettings.RefillAmount"
                        id="refillAmount"
                        class="full-width" />
            </div>

            <div v-if="this.accountSettings.ShowAccountOrGlCode" class="form-group">
              <ed-input @input="(o) => { addObjectUser.AccountOrGlCode = o; }"
                        type="text"
                        helptipHeader=""
                        field-label="Account/Gl Code"
                        :required="addObjectUser.HasAccountingSystemSync"
                        validation
                        @validate='addCard'
                        :error="errorGLCode"
                        v-model="addObjectUser.AccountOrGlCode"
                        class="full-width">
              </ed-input>
            </div>

            <div v-if="this.accountSettings.AccountingSystem.SyncEvedPayCards" class="form-group">
              <label>PSL+ Vendor</label>
              <div style="display: flex;">
                <input type="checkbox" v-model="addObjectUser.HasAccountingSystemSync" id="push-to-accounting-system">
                <label for="push-to-accounting-system">Sync Card data with PSL+</label>
              </div>
            </div>

            <div class="form-group">
              <div class="checkbox-label-container">
                <label for="physicalCard" class="large-text">Single Physical Card - <span class="optional-text">optional</span></label>
              </div>
              <div class="checkbox-label-container">
                <small class="small-italic" style="font-size: 0.8em; display: inline;">
                  When ordering multiple physical cards, please use the Request Physical Batch button
                </small>
              </div>
              <div style="display: flex;">
                <input type="checkbox" v-model="physicalCard" id="physicalCard">
                <label for="physicalCard">Send the Cardholder a physical card - provide mailing address below:</label>
              </div>
            </div>

            <div class="form-group" :class="{ 'disabled': !physicalCard }">
              <label for="country">Country</label>
              <ed-select @input="(o) => { mailingAddress.country = o; this.countryIncomplete = false; }"
                         placeholder="Select a country..."
                         :options="countries"
                         v-model="formData.country"
                         label="CountryName"
                         track-by="CountryID"
                         :openEndpoint='api.$.profile.getAllCountries'
                         @select="countrySelected($event)"
                         @open="fetchCountriesOnOpen"
                         @keyup="fetchCountries"
                         @close="restoreCountries"
                         :disabled="disableControls"
                         name='supplier_country' />
            </div>

            <div class="form-group" :class="{ 'disabled': !physicalCard }">
              <label for="name">Name</label>
              <ed-input @input="(o) => { mailingAddress.name = o; this.nameIncomplete = false; }"
                        type="text"
                        v-model="name"
                        id="name"
                        :error="nameIncomplete"
                        placeholder="Name"
                        maxlength="40"
                        class="full-width" />
            </div>

            <div class="form-group" :class="{ 'disabled': !physicalCard }">
              <label for="address">Address</label>
              <ed-input @input="(o) => { mailingAddress.line1 = o; this.addressIncomplete = false; }"
                        type="text"
                        v-model="address"
                        id="address"
                        :error="addressIncomplete"
                        placeholder="Address"
                        maxlength="26"
                        class="full-width" />
            </div>
            <div class="form-group" :class="{ 'disabled': !physicalCard }">
              <ed-input @input="(o) => mailingAddress.line2 = o"
                        type="text"
                        field-label="Address Line 2"
                        v-model="addressLine2"
                        id="addressLine2"
                        placeholder="Address Line 2"
                        class="full-width" />
            </div>

            <div class="form-group" :class="{ 'disabled': !physicalCard }">
              <label for="city">City</label>
              <ed-input @input="(o) => { mailingAddress.city = o; this.cityIncomplete = false; }"
                        type="text"
                        v-model="city"
                        id="city"
                        :error="cityIncomplete"
                        placeholder="City"
                        maxlength="25"
                        class="full-width" />
            </div>

            <div class="form-group" :class="{ 'disabled': !physicalCard }">
              <div class="input-group">
                <div class="input-group-item">
                  <label for="state">State</label>
                  <ed-select @input="(o) => { mailingAddress.state = o; this.stateIncomplete = false; }"
                             v-if="isUS()"
                             :options="USstates"
                             v-model="formData.state"
                             @open="fetchUSStatesOnOpen"
                             @keyup="fetchUSStates"
                             @close="restoreUSStates"
                             label="StateName"
                             :error="stateIncomplete"
                             track-by="StateID"
                             name='us_states' />
                  <ed-select @input="(o) => { mailingAddress.canadaState = o; this.stateIncomplete = false; }"
                             v-if="isCanada()"
                             placeholder="Select a region"
                             :options="canadaStates"
                             v-model="formData.canadaState"
                             @open="fetchCanadaStatesOnOpen"
                             @keyup="fetchCanadaStates"
                             @close="restoreCanadaStates"
                             label="StateName"
                             :disabled="disableControls"
                             name='canada_states' />
                  <ed-input @input="(o) => { mailingAddress.region = o; this.stateIncomplete = false; }"
                            v-if="!formData.country || !isCanada() && !isUS()"
                            placeholder="Select a region"
                            v-model="formData.region"
                            :disabled="disableControls"
                            name='state_region' />
                </div>
                <div class="input-group-item">
                  <label for="zipCode">Zip Code</label>
                  <ed-input @input="(o) => { mailingAddress.zip = o; this.zipCodeIncomplete = false; }"
                            type="text"
                            v-model="zipCode"
                            id="zipCode"
                            :error="zipCodeIncomplete"
                            placeholder="Zip Code"
                            maxlength="5"
                            class="full-width" />
                </div>
              </div>
              <hr v-if="this.accountSettings.AllowRushPhysicalCards" />
              <div class="form-group" v-if="this.accountSettings.AllowRushPhysicalCards">
                <label for="rushOrder">Rush Order - <span class="optional-text">optional</span></label>
                <p class="small">
                  For a fee, a physical card can be sent via expedited shipping.
                </p>
                <div class="form-group" style="display: flex;">
                  <input type="checkbox" v-model="rushOrder" id="rushOrder">
                  <label for="rushOrder">Rush shipment of this card</label>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ed-modal>
    </form>
  </span>
</template>

<script>

import AddUser from './AddUser.vue'

export default {
  components: {
    AddUser
  },
  data: function () {
    return {
      formData: {
        city: '',
        postalCode: '',
        originalCountryName: '',
        country: '',
        state: {
          StateID: 0,
          StateName: ''
        },
        canadaState: {
          StateID: 0,
          StateName: ''
        },
        region: ''
      },

      USstates: [],
      USoriginalStates: [],

      canadaStates: [],
      originalCanadaStates: [],

      countries: [],
      initialLoad: '',
      RefillSettings: { RefillAmount: null, RefillThreshold: null },
      originalCountries: [],
      show: true,
      selectUser: '',
      selectDivision: '',
      disableControls: false,
      physicalCard: false,
      users: [],
      showAddCard: false,
      savingCardInProgress: false,
      showAddUser: false,
      mailingAddress: {},
      addObjectUser: { Holder: { Mobile: {} } },
      divisions: [],
      selectedDivisionId: 0,
      selectedAccountId: '',
      rushOrder: false,
      initialLoadError: false,
      cardHolderPhoneNumberError: false,
      holderIncomplete: false,
      countryIncomplete: false,
      nameIncomplete: false,
      addressIncomplete: false,
      cityIncomplete: false,
      stateIncomplete: false,
      zipCodeIncomplete: false,
      errorMessage: 'The amount cannot be greater than the available funds.',
      errorPhoneMessage: 'The phone number cannot contain characters other than digits.',
      errorGLCode: false,
      errorMessageGLCode: 'Required',
      pageSize: 50,
      card: {
        users: [],
        pageIndex: 0,
        hasMore: true,
        isLoading: false,
        searchTerm: ''
      },
      admin: {
        users: [],
        pageIndex: 0,
        hasMore: true,
        isLoading: false,
        searchTerm: ''
      },
      hasData: false
    }
  },

  computed: {
    dateFormat() {
      let dateSettings = this.$root.getUser.sessionSettings.DateAndTimeFormats
      return dateSettings.MomentDateFormat || 'MM/DD/YYYY'
      // return dateSettings.JsDateFormat || dateSettings.JsDateFormatCompany || dateSettings.JsDateFormatDefault || ''
    }
  },

  watch: {
    showAddCard: function (newVal, oldVal) {
      if (newVal) {
        this.populateFormData(this.formData)
      }
    }
  },

  props: {
    divisionId: '',
    accountId: '',
    divisionName: 'Division',
    divisionsName: 'Divisions',
    account: Object,
    accountSettings: Object
  },

  methods: {
    async addCard() {
      this.savingCardInProgress = true

      if (!this.isValidCardForSaving()) {
        this.savingCardInProgress = false
        return
      }

      if (this.addObjectUser.AccountOrGlCode) {
        this.addObjectUser.AccountOrGlCode = this.addObjectUser.AccountOrGlCode.trim()
      }

      try {
        let payload = {
          AccountID: this.accountId ? this.accountId : this.selectedAccountId,
          ...this.addObjectUser,
          IsPhysicalCard: this.physicalCard,
          RefillSettings: this.RefillSettings
        }

        if (this.physicalCard) {
          const shippingInfo = {
            Shipping: {
              IsRush: this.rushOrder,
              Name: this.mailingAddress.name,
              Address: {
                Country: { Id: 186 },
                Address1: this.mailingAddress.line1,
                Address2: this.mailingAddress.line2,
                City: this.mailingAddress.city,
                State: { Id: this.mailingAddress.state.StateID },
                Zip: this.mailingAddress.zip
              }
            }
          }
          payload = { ...payload, ...shippingInfo }
        }

        const response = await this.api.post(this.api.$.evedpaycard.list, payload)

        if (!response.data) {
          this.savingCardInProgress = false
          throw new Error('Error during the request')
        }

        this.$emit('success')
        this.initialLoadError = false
        this.cardHolderPhoneNumberError = false
        this.holderIncomplete = false
        this.countryIncomplete = false
        this.nameIncomplete = false
        this.addressIncomplete = false
        this.cityIncomplete = false
        this.stateIncomplete = false
        this.zipCodeIncomplete = false
        this.errorGLCode = false
        this.showAddCard = false
        this.showAddUser = false
      } catch (error) {
        this.savingCardInProgress = false
        console.error('Error during the request:', error)
      }
    },

    isValidCardForSaving() {
      if (!this.selectUser) {
        this.holderIncomplete = true
        return false
      } else {
        this.holderIncomplete = false
      }

      if (this.initialLoad < 0 || this.initialLoad > this.account.Available) {
        this.initialLoadError = true
        return false
      } else {
        this.initialLoadError = false
      }

      if (/\D/.test(this.addObjectUser.Holder.Mobile.PhoneNumber)) {
        this.cardHolderPhoneNumberError = true
        return false
      } else {
        this.cardHolderPhoneNumberError = false
      }

      if (this.addObjectUser.HasAccountingSystemSync) {
        this.addObjectUser.AccountOrGlCode = (this.addObjectUser.AccountOrGlCode || '').trim()
        if (!this.addObjectUser.AccountOrGlCode.trim()) {
          this.errorGLCode = true
          return false
        } else {
          this.errorGLCode = false
        }
      } else {
        this.errorGLCode = false
      }

      if (this.physicalCard) {
        if (!this.formData.country) {
          this.countryIncomplete = true
          return false
        } else {
          this.countryIncomplete = false
        }

        if (!this.name) {
          this.nameIncomplete = true
          return false
        } else {
          this.nameIncomplete = false
        }

        if (!this.address) {
          this.addressIncomplete = true
          return false
        } else {
          this.addressIncomplete = false
        }

        if (!this.city) {
          this.cityIncomplete = true
          return false
        } else {
          this.cityIncomplete = false
        }

        if (this.isUS()) {
          if (!this.formData.state || !this.formData.state.StateID) {
            this.stateIncomplete = true
            return false
          } else {
            this.stateIncomplete = false
          }
        }

        if (!this.zipCode) {
          this.zipCodeIncomplete = true
          return false
        } else {
          this.zipCodeIncomplete = false
        }
      }

      return true
    },

    onOpenCard() {
      this.card.searchTerm = ''
      this.loadUsers('card', true)
    },

    onOpenAdmin() {
      this.admin.searchTerm = ''
      this.loadUsers('admin', true)
    },

    async loadUsers(type, reset = false) {
      const loader = this[type]
      if (loader.isLoading) return

      if (reset) {
        loader.pageIndex = 0
        loader.users = []
        loader.hasMore = true
      }
      if (!loader.hasMore) return

      loader.isLoading = true
      try {
        const { data } = await this.api.get(
          this.api.$.divisionUsers,
          {
            params: {
              divisionId: this.divisionId || this.selectedDivisionId,
              pageIndex: loader.pageIndex,
              pageSize: this.pageSize,
              searchTerm: loader.searchTerm
            }
          }
        )

        const { Items, HasMore } = data

        const mapped = Items
          .sort((a, b) =>
            (`${a.FirstName} ${a.LastName}`)
              .localeCompare(`${b.FirstName} ${b.LastName}`)
          )
          .map(u => ({
            Id: u.VendorUserId,
            Name: `${u.FirstName} ${u.LastName}`,
            Mobile: {
              CountryCode: u.Mobile.CountryCode || 'US',
              PhoneCode: u.Mobile.PhoneCode || '+1',
              PhoneNumber: u.Mobile.PhoneNumber || ''
            },
            DateOfBirth: global.moment(u.DateOfBirth)
              .format(this.$root.getUser.sessionSettings.DateAndTimeFormats.MomentDateFormat)
          }))

        loader.users = reset
          ? mapped
          : loader.users.concat(mapped)
        loader.hasMore = HasMore
        loader.pageIndex += 1
        this.hasData = true
      } catch (e) {
        console.error('loadUsers error', e)
        this.hasData = false
      } finally {
        loader.isLoading = false
      }
    },

    showModal(showAddCard) {
      if (!this.initialLoadError && !this.holderIncomplete) {
        this.showAddCard = !showAddCard
        this.resetModalFields()
      }
      this.showAddCard = !showAddCard
      this.resetModalFields()
      this.savingCardInProgress = false
    },

    showModalUser(showAddUser) {
      this.showAddUser = !showAddUser
    },

    closeAddUserModal() {
      this.showAddUser = false
    },

    cardActionDisabled(actionName) {
      return !!this.actionDisabledReason(actionName)
    },

    actionDisabledReason(actionName) {
      if ((this.account.Funded === 0 || this.account.Available === 0) && actionName === 'addCard') {
        return 'No funds available to add a new card.'
      }
    },

    filterAdminOptions(option) {
      if (this.addObjectUser.Holder && this.addObjectUser.Holder.Id) {
        return option.Id !== this.addObjectUser.Holder.Id
      } else {
        return false
      }
    },

    resetModalFields() {
      this.$nextTick(() => {
        this.$refs.formRef.reset()
        this.selectUser = ''
        this.selectUsers = []
        this.physicalCard = false
        this.formData.state = ''
        this.formData.canadaState = ''
        this.selectDivision = ''
        this.selectedDivisionId = 0
        this.mailingAddress = {}
        this.addObjectUser = { Holder: { Mobile: {} } }
        this.selectedAccountId = ''
        this.initialLoad = ''
        this.RefillSettings = { RefillAmount: null, RefillThreshold: null }
        this.initialLoadError = false
        this.cardHolderPhoneNumberError = false
        this.holderIncomplete = false
        this.countryIncomplete = false
        this.nameIncomplete = false
        this.addressIncomplete = false
        this.cityIncomplete = false
        this.stateIncomplete = false
        this.zipCodeIncomplete = false
        this.errorGLCode = false
      })
    },

    fetchCountriesOnOpen() {
      if (this.countries.length) {
      } else {
        this.api.forms.post(
          this.api.$.profile.getAllCountries,
          {
            q: ''
          }
        ).then(r => {
          if (r && r.data && r.data.Data) {
            this.countries = r.data.Data
            this.originalCountries = r.data.Data
          }
        }
        ).catch(e => console.warn(e))
      }
    },

    onCountrySelect({ dialCode, iso2 }) {
      this.addObjectUser.Holder.Mobile.CountryCode = iso2
      this.addObjectUser.Holder.Mobile.PhoneCode = '+' + dialCode
    },

    onSelectUser(user) {
      if (!user) {
        this.addObjectUser.Holder = { Mobile: {} }
        return
      }

      this.addObjectUser.Holder = user
      this.initialLoadError = false
      this.cardHolderPhoneNumberError = false

      let phoneCode = user.Mobile.PhoneCode.replace(/\+/, '')
      this.$refs.countryCodeComponent.choose({ iso2: user.Mobile.CountryCode, dialCode: phoneCode })
    },

    fetchCountries(value) {
      this.counter || this.api.forms.post(
        this.api.$.profile.getAllCountries,
        {
          q: value || ''
        }
      ).then(r => {
        this.counter = 0
        if (r && r.data && r.data.Data) {
          this.countries = r.data.Data
        }
      }
      ).catch(e => {
        console.warn(e)
        this.counter = 0
      })
      this.counter = 1
    },

    async fetchUserDivisions() {
      this.divisions = []
      if (this.accountId === '') {
        this.api.get(this.api.$.evedpaycard.division)
          .then(response => {
            if (response && response.data) {
              for (var account of response.data) {
                this.divisions.push({
                  Id: account.DivisionId,
                  Name: account.DivisionName,
                  AccountId: account.AccountId
                })
              }
            }
          })
          .catch(error => {
            console.warn(error)
          })
      }
    },

    restoreCountries() {
      if (this.counter === 0) {
        this.countries = utils.copy(this.originalCountries)
      }
    },

    isUS() {
      return this.formData.country && this.formData.country.CountryName === 'United States'
    },
    GetStatesUSURL() {
      return this.api.$.profile.getAllStates
    },

    isCanada() {
      return this.formData.country && this.formData.country.CountryName === 'Canada'
    },
    GetStatesCanadaURL() {
      return this.api.$.profile.getCanadaStates
    },

    // Fetch States on OPEN
    fetchUSStatesOnOpen() {
      var url = this.GetStatesUSURL()
      this.fetchStatesOnOpen(this.USstates, url)
    },
    fetchCanadaStatesOnOpen() {
      var url = this.GetStatesCanadaURL()
      this.fetchStatesOnOpen(this.canadaStates, url)
    },
    fetchStatesOnOpen(states, url) {
      if (states.length && (this.isCanada() || this.isUS())) {
      } else {
        this.api.forms.post(
          url,
          {
            q: ''
          }
        ).then(r => {
          if (r && r.data && r.data.Data) {
            this.mapStatesDataOnOpen(r.data.Data)
          }
        }
        ).catch(e => console.warn(e))
      }
    },
    mapStatesDataOnOpen(data) {
      if (this.isUS()) {
        this.USstates = this.USoriginalStates = data
      } else {
        this.canadaStates = this.originalCanadaStates = data
      }
    },

    // Fetch States on search text
    fetchUSStates(value) {
      var url = this.GetStatesUSURL()
      this.fetchStates(value, url)
    },
    fetchCanadaStates(value) {
      var url = this.GetStatesCanadaURL()
      this.fetchStates(value, url)
    },
    fetchStates(value, url) {
      this.counter || this.api.forms.post(
        url,
        {
          q: value || ''
        }
      ).then(r => {
        this.counter = 0
        if (r && r.data && r.data.Data) {
          this.mapStatesDataOnFetch(r.data.Data)
        }
      }
      ).catch(e => {
        console.warn(e)
        this.counter = 0
      })
      this.counter = 1
    },
    mapStatesDataOnFetch(data) {
      if (this.isUS()) {
        this.USstates = data
      } else {
        this.canadaStates = data
      }
    },

    // Restore States
    restoreUSStates() {
      this.restoreStates(this.USstates, this.USoriginalStates)
    },
    restoreCanadaStates() {
      this.restoreStates(this.canadaStates, this.originalCanadaStates)
    },
    restoreStates(states, originalStates) {
      if (this.counter === 0) {
        this.states = utils.copy(originalStates)
      }
    },
    populateFormData(data) {
      if (data.CompanyName !== null) {
        this.formData = {
          city: data.City,
          postalCode: data.Zip,
          originalCountryName: data.CountryName,
          country: {
            'CountryID': 186,
            'CountryName': 'United States'
          },
          state: {
            StateID: data.isCanada === 'False' && data.StateID || 0,
            StateName: data.isCanada === 'False' && data.State || ''
          },
          canadaState: {
            StateID: data.isCanada === 'True' && data.StateID || 0,
            StateName: data.isCanada === 'True' && data.State || ''
          },
          region: data.Region
        }
        this.disableControls = true
      } else {
        this.disableControls = false
      }
    },
    countrySelected(e) {
      this.doDisplayCountryError = false
      this.resetState(e.CountryName)
    },

    resetState(countryName) {
      if (this.originalCountryName !== '' && this.originalCountryName !== countryName) {
        this.formData.state = {
          StateID: 0,
          StateName: ''
        }
        this.formData.canadaState = {
          StateID: 0,
          StateName: ''
        }
        this.formData.region = ''
      } else {
        this.originalCountryName = countryName
      }
    }
  }
}
</script>

<style lang="scss" scoped>
  .add-card-overlay {
    background-color: white;
    padding: 20px;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  .add-card-overlay label {
    margin-bottom: 0;
  }

  .add-card-overlay input[type="checkbox"] + label {
    font-weight: 300;
    margin-left: 7px; 
    margin-top: 3px;
    font-size: smaller;
  }

  .form-group {
    margin-bottom: 10px;
  }

  .full-width {
    width: 100%;
  }

  .error {
    color: red;
  }

  .form-group label {
    vertical-align: middle;
  }

  .input-group {
    display: flex;
    flex-wrap: wrap;
  }

  .input-group-item {
    flex: 1;
    margin-right: 10px;
  }

  .input-group-item:last-child {
  margin-right: 0;
}

  .small-italic {
    font-size: smaller; 
    font-style: italic; 
  }

  .checkbox-label-container {
    align-items: center; 
  }

  ::-webkit-input-placeholder {
    font-size: 12px;
  }

  :-moz-placeholder {
    font-size: 12px;
  }

  ::-moz-placeholder {
    font-size: 12px;
  }

  :-ms-input-placeholder {
    font-size: 12px;
  }

  .optional-text {
    font-style: italic;
    opacity: 0.6;
  }

  .disabled {
    opacity: 0.6;
    pointer-events: none;
  }

  .selected-users {
    display: flex;
    flex-wrap: wrap;
    font-size: 10px;
  }

  .user-box {
    border: 1px solid #ccc;
    border-radius: 20px;
    padding: 4px;
    margin-right: 8px;
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    background-color: #f0f0f0;
    position: relative;
  }

  .user-name {
    margin-right: 7px;
  }

  .remove-button {
    cursor: pointer;
    font-size: 10px;
    position: absolute;
    top: 2px;
    right: 2px;
  }

  .dropdown {
    border-radius: unset;
    height: 34px;
    width:90px;
    ul.dropdown-list{
      width: 345px;
    }
  }
  div.vue-country-select:focus-within{
    box-shadow: 0px 0px 0px 1px #a9d85f;
    border-color: #a9d85f;
  }
  .dropdown >>> span.country-code{
    color: #333;
    font-weight: 400;
    font-size: 0.85rem;
    padding-left:8px !important;
  }
</style>



// WEBPACK FOOTER //
// src/components/_modules/Programs/Card-list/AddCard.vue